import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { auth } from "../api/auth";
import type { Role } from "../api/auth";
import { ApiError, setActiveVpsId } from "../api/client";

interface AuthState {
  status: "checking" | "anon" | "authed";
  userId: number | null;
  username: string | null;
  role: Role | null;
  activeVpsId: number | null;
}

interface AuthCtxValue extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-fetch /me — call after enter/exit to refresh local state. */
  recheck: () => Promise<void>;
  /** POST /api/auth/enter/:vps_id and update local state on success. */
  enterVps: (vpsId: number) => Promise<void>;
  /** Exit the current VPS (clears the auto-prefix). Stays logged in. */
  exitVps: () => void;
}

const AuthCtx = createContext<AuthCtxValue | null>(null);

const anonState: AuthState = {
  status: "anon",
  userId: null,
  username: null,
  role: null,
  activeVpsId: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: "checking",
    userId: null,
    username: null,
    role: null,
    activeVpsId: null,
  });

  const applyMe = async () => {
    try {
      const me = await auth.me();
      setActiveVpsId(me.active_vps_id);
      setState({
        status: "authed",
        userId: me.user_id,
        username: me.username,
        role: me.role,
        activeVpsId: me.active_vps_id,
      });
    } catch (e) {
      setActiveVpsId(null);
      if (e instanceof ApiError && e.status === 401) {
        setState(anonState);
      } else {
        // Network / server down — treat as anonymous so the login screen
        // shows; better UX than a perpetual spinner.
        setState(anonState);
      }
    }
  };

  useEffect(() => {
    void applyMe();
  }, []);

  const value = useMemo<AuthCtxValue>(
    () => ({
      ...state,
      login: async (username, password) => {
        await auth.login(username, password);
        // /me carries active_vps_id + role; trust the server view.
        await applyMe();
      },
      logout: async () => {
        try {
          await auth.logout();
        } catch {
          // best-effort
        }
        setActiveVpsId(null);
        setState(anonState);
      },
      recheck: applyMe,
      enterVps: async (vpsId) => {
        await auth.enterVps(vpsId);
        await applyMe();
      },
      exitVps: () => {
        setActiveVpsId(null);
        setState((s) => ({ ...s, activeVpsId: null }));
        // We don't have a server-side "exit" — the cookie still carries
        // active_vps_id until it expires or the user enters another. The
        // UI just stops sending VPS-scoped calls.
      },
    }),
    [state],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthCtxValue {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
